import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mergePlansForMultiCurrencyDisplay,
  parseSubscriptionPlansResponse,
  buildPricingTiers,
  PLAN_FEATURES,
  formatPlanPrice,
  getMonthlyEquivalent,
} from '../utils/pricing';
import './Pricing.css';

const API_PLANS_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')}/payment/subscription-plans/`
  : null;

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const [mergedPlans, setMergedPlans] = useState([]);
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [loading, setLoading] = useState(!!API_PLANS_URL);
  const [error, setError] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');

  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const tiers = useMemo(
    () => buildPricingTiers(mergedPlans, billingInterval, preferredCurrency),
    [mergedPlans, billingInterval, preferredCurrency],
  );

  const sortedTiers = useMemo(() => {
    const order = isRTL ? ['enterprise', 'base'] : ['base', 'enterprise'];
    return [...tiers].sort((a, b) => order.indexOf(a.planKey) - order.indexOf(b.planKey));
  }, [tiers, isRTL]);

  useEffect(() => {
    if (!API_PLANS_URL) {
      setError(t('pricingApiNotConfigured'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setLoading(true);

    const url = `${API_PLANS_URL}?meta=1&lang=${encodeURIComponent(i18n.language)}`;
    fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Accept-Language': i18n.language,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load plans');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const { plans, currency } = parseSubscriptionPlansResponse(data, i18n.language);
        setPreferredCurrency(currency || 'USD');
        setMergedPlans(mergePlansForMultiCurrencyDisplay(plans));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t('pricingLoadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [i18n.language, t]);

  const handleContactUs = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartNow = () => {
    const appUrl = process.env.REACT_APP_APP_URL || 'https://app.glowapp.me';
    if (appUrl) window.open(appUrl, '_blank');
  };

  if (loading) {
    return (
      <section id="pricing" className="pricing" aria-label={t('pricing')}>
        <div className="container">
          <p className="section-eyebrow">{t('pricing')}</p>
          <h2 className="section-title">{t('pricing')}</h2>
          <p className="section-subtitle">{t('pricingDescription')}</p>
          <div className="pricing-loading" aria-busy="true">{t('loading')}</div>
        </div>
      </section>
    );
  }

  if (error || sortedTiers.length === 0) {
    return (
      <section id="pricing" className="pricing" aria-label={t('pricing')}>
        <div className="container">
          <h2 className="section-title">{t('pricing')}</h2>
          <p className="pricing-error">{error || t('pricingNoPlans')}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="pricing" aria-label={t('pricing')}>
      <div className="container">
        <p className="section-eyebrow">{t('pricing')}</p>
        <h2 className="section-title">{t('pricing')}</h2>
        <p className="section-subtitle">{t('pricingDescription')}</p>

        <div className="pricing-toggle-wrap" role="group" aria-label={t('billingInterval')}>
          <button
            type="button"
            className={`pricing-toggle-btn ${billingInterval === 'month' ? 'active' : ''}`}
            onClick={() => setBillingInterval('month')}
            aria-pressed={billingInterval === 'month'}
          >
            {t('monthly')}
          </button>
          <button
            type="button"
            className={`pricing-toggle-btn ${billingInterval === 'year' ? 'active' : ''}`}
            onClick={() => setBillingInterval('year')}
            aria-pressed={billingInterval === 'year'}
          >
            {t('annually')}
          </button>
        </div>

        <div className="pricing-grid">
          {sortedTiers.map((tier) => {
            const isContactUs = tier.isManualEnterprise;
            const planKey = tier.planKey;
            const displayName = t(`planName${planKey.charAt(0).toUpperCase()}${planKey.slice(1)}`);
            const featureKeys = PLAN_FEATURES[planKey] || [];
            const isPopular = planKey === 'base';
            const display = tier.display;
            const seatDisplay = tier.seatDisplay;

            return (
              <article key={planKey} className={`pricing-card ${isPopular ? 'popular' : ''}`}>
                {isPopular && (
                  <div className="popular-badge" aria-label={t('mostPopular')}>{t('mostPopular')}</div>
                )}
                <h3 className="plan-name">{displayName}</h3>
                {featureKeys.length > 0 && (
                  <ul className="plan-features">
                    {featureKeys.map((fk) => (
                      <li key={fk}>
                        <span className="check-icon" aria-hidden="true">✓</span>
                        {t(fk)}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="plan-price-block">
                  {isContactUs ? (
                    <div className="plan-price plan-price--primary">{t('customPrice')}</div>
                  ) : billingInterval === 'year' && getMonthlyEquivalent(tier, display?.amount) != null ? (
                    <>
                      <div className="plan-price plan-price--primary">
                        {formatPlanPrice(getMonthlyEquivalent(tier, display.amount), display.currency, 'month', t)}
                      </div>
                      <div className="plan-price plan-price--secondary">
                        {formatPlanPrice(display.amount, display.currency, 'year', t)}
                      </div>
                    </>
                  ) : (
                    <div className="plan-price plan-price--primary">
                      {formatPlanPrice(display.amount, display.currency, billingInterval, t)}
                    </div>
                  )}
                  {!isContactUs && seatDisplay && (
                    <div className="plan-price plan-price--secondary">
                      {t('extraSeatPrice', {
                        price: formatPlanPrice(seatDisplay.amount, seatDisplay.currency, billingInterval, t),
                      })}
                    </div>
                  )}
                  {!isContactUs && (
                    <p className="plan-seat-note">{t('pricingSeatNote')}</p>
                  )}
                </div>
                {isContactUs ? (
                  <button type="button" onClick={handleContactUs} className="btn btn-primary plan-cta">
                    {t('contactUs')}
                  </button>
                ) : (
                  <button type="button" onClick={handleStartNow} className="btn btn-primary plan-cta">
                    {t('startNow')}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
